import { S } from './state.js';
// Responsive geometry for the modal card overlays (shop / boss reward / item cache / level-up).
// The old layout was a fixed N-across row sized for a desktop window: on a portrait phone each
// card collapsed to ~90px (unreadable) and on a short landscape phone the footer fell off-screen.
//
// cardLayout() picks a column count from the viewport WIDTH, then — if that many rows wouldn't fit
// the viewport HEIGHT — adds columns to cut rows, so the whole grid always fits without scrolling.
// Portrait phones get 1 wide column; short landscape gets a single row; desktop keeps 3 across.
// Card internals are anchored to the card's top (icon/title) and bottom (button) and reflow at any
// width, so they only need cardH >= ~196, which the layout guarantees.
//
// opts: { maxW, header, footer, minCardW, idealCardH, minCardH, margin, pad, gap }
//   header = vertical band reserved for the title/subtitle (cards start below it)
//   footer = vertical band reserved below the grid for the overlay's own controls
// Returns geometry + cardX(i)/cardY(i) helpers (i indexes row-major across the grid).
export function cardLayout(n, opts = {}) {
  const M = opts.margin ?? 16, PAD = opts.pad ?? 28, GAP = opts.gap ?? 16;
  const HEADER = opts.header ?? 92, FOOTER = opts.footer ?? 56;
  const MINW = opts.minCardW ?? 224, IDEALH = opts.idealCardH ?? 248, MINH = opts.minCardH ?? 196;
  const maxPW = opts.maxW ?? 820;

  const pw = Math.min(maxPW, S.W - 2 * M);
  const avail = pw - 2 * PAD;
  // how many cards fit across by width…
  const colsByWidth = Math.max(1, Math.min(n, Math.floor((avail + GAP) / (MINW + GAP))));
  // …but never so many ROWS that a card would be shorter than MINH
  const maxGridH = (S.H - 2 * M) - HEADER - FOOTER;
  const maxRows = Math.max(1, Math.floor((maxGridH + GAP) / (MINH + GAP)));
  const cols = Math.max(1, Math.min(n, Math.max(colsByWidth, Math.ceil(n / maxRows))));
  const rows = Math.ceil(n / cols);
  const cardW = (avail - (cols - 1) * GAP) / cols;

  const wantPH = HEADER + rows * IDEALH + (rows - 1) * GAP + FOOTER;
  const ph = Math.min(wantPH, S.H - 2 * M);
  const gridH = ph - HEADER - FOOTER;
  const cardH = (gridH - (rows - 1) * GAP) / rows;

  const px = (S.W - pw) / 2, py = (S.H - ph) / 2;
  const gridTop = py + HEADER;
  return {
    px, py, pw, ph, cols, rows, cardW, cardH, gridTop, gap: GAP, pad: PAD, header: HEADER, footer: FOOTER,
    cardX: (i) => px + PAD + (i % cols) * (cardW + GAP),
    cardY: (i) => gridTop + Math.floor(i / cols) * (cardH + GAP),
  };
}
